/// <amd-module name="TFS/GlobalBanner/Components/GlobalToast" />
import * as React from "react";
import { GlobalMessagesChangedEvents, IGlobalMessagesService, IToast } from "TFS/GlobalBanner/GlobalMessagesService.Types";
import { ICancelablePromise } from "VSS/Core/Util/Promise";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IToast as IToastComponent } from "VSSUI/Toast.Types";

interface IGlobalToastComponentState {
    toast?: IToast;
}

/**
 * Page-level component for toasts
 */
export class GlobalToastComponent extends VssComponent<{}, IGlobalToastComponentState> {
    public static componentType = "globalToast";
    private globalMessagesService: IGlobalMessagesService;
    private toastRef: React.RefObject<IToastComponent> = React.createRef<IToastComponent>();
    private toastQueue: IToast[] = [];
    private toastExpiresTimeoutId: number = -1;
    private keyId: number = 0;
    private fadeOutPromiseCancelable?: ICancelablePromise<void>;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.globalMessagesService = context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        this.state = {};
    }

    public render() {
        const { toast } = this.state;
        return toast ? (
            <WrappedComponent
                wrappedType="toast"
                dependencies={["ms.vss-features.ui-layer-content"]}
                key={"globalToast" + this.keyId}
                wrappedRef={this.toastRef}
                {...toast}
            />
        ) : null;
    }

    public componentDidMount() {
        this.globalMessagesService.subscribe(this.onNewToast, GlobalMessagesChangedEvents.GlobalToastChangedEvent);
    }

    public componentWillUnmount() {
        this.fadeOutPromiseCancelable && this.fadeOutPromiseCancelable.cancel();
        clearTimeout(this.toastExpiresTimeoutId);
        this.globalMessagesService.unsubscribe(this.onNewToast, GlobalMessagesChangedEvents.GlobalToastChangedEvent);
    }

    private onNewToast = (newToastData: IToast) => {
        if (newToastData.forceOverrideExisting) {
            this.fadeOutAndRenderNew(newToastData);
        } else if (!this.state.toast) {
            this.updateCurrentToast(newToastData);
        } else {
            this.toastQueue.push(newToastData);
        }
    };

    private onToastExpires = () => {
        const nextFromQueue = this.toastQueue.shift();

        if (nextFromQueue) {
            this.fadeOutAndRenderNew(nextFromQueue);
        } else {
            this.clearToast();
        }
    };

    private fadeOutAndRenderNew(newToast: IToast) {
        if (this.toastRef.current) {
            this.fadeOutPromiseCancelable = this.toastRef.current.fadeOut();
            this.fadeOutPromiseCancelable.promise
                .then(() => {
                    this.updateCurrentToast(newToast);
                })
                .catch(reason => {
                    if (reason.isCanceled) {
                        return;
                    } else {
                        throw reason;
                    }
                });
        } else {
            this.updateCurrentToast(newToast);
        }
    }

    private updateCurrentToast(newToastData: IToast) {
        this.keyId += 1;
        clearTimeout(this.toastExpiresTimeoutId);
        this.setState({ toast: newToastData }, () => {
            this.toastExpiresTimeoutId = setTimeout(this.onToastExpires, newToastData.duration);
        });
    }

    private clearToast() {
        if (this.toastRef.current) {
            this.fadeOutPromiseCancelable && this.fadeOutPromiseCancelable.cancel();
            this.fadeOutPromiseCancelable = this.toastRef.current.fadeOut();
            this.fadeOutPromiseCancelable.promise
                .then(() => {
                    this.setState({ toast: undefined });
                })
                .catch(reason => {
                    if (reason.isCanceled) {
                        return;
                    } else {
                        throw reason;
                    }
                });
        }
    }
}

// Register the global toast with the layout manager
VssComponent.register(GlobalToastComponent.componentType, GlobalToastComponent);
